import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { ButtonLink, Card, Container, Heading, PageHeader, Text } from '@bn-apps/dim-ui-react';
import { NetworkRailIllustration } from './NetworkRailIllustration';
import LatestStatusReportCard from '@components/statusReports/LatestStatusReportCard';
import styles from './LandingPage.module.scss';
import appStyles from '@/App.module.scss';
import { Download } from '@bn-apps/design-system-icons';
import routes from '@config/routes';
import { useAuth } from '@hooks/useAuth';

const LandingPage = (): JSX.Element => {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <Container className={appStyles.pageContent}>
      {/* Hero Section */}
      <div className="bn-mbe-2000">
        <PageHeader title={t('landing.hero.title')} />

        <div className={styles.heroContent}>
          <div className={styles.heroLeft}>
            <div className={styles.heroHeading}>
              <Heading level={2} className="bn-mbe-600">
                {t('landing.hero.subtitle')}
              </Heading>
              <Text variant="lead" className="bn-mbe-600">
                {t('landing.hero.description')}
              </Text>
            </div>
            <div className={styles.statsCards}>
              <Card tone="success" elevation="raised" padding="lg" gap="none" className={styles.statCard}>
                <div className={styles.statCardContent}>
                  <div className={styles.statIconAndHeading}>
                    <svg
                      className={styles.statIcon}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 -960 960 960"
                      fill="currentColor"
                    >
                      <path d="M180-347.69V-720q0-46.08 25-73.73 25-27.65 66.73-42.23 41.73-14.58 95.96-19.31Q421.92-860 480-860q62.15 0 116.81 4.73 54.65 4.73 95.46 19.31 40.81 14.58 64.27 42.23Q780-766.08 780-720v372.31q0 53.61-37.04 90.65Q705.92-220 652.31-220l60 60v20h-56.92l-80-80H384.61l-80 80h-56.92v-20l60-60q-53.61 0-90.65-37.04Q180-294.08 180-347.69ZM480-800q-111.77 0-163.08 14.04-51.31 14.04-65.84 36.73h460.31q-12.7-23.92-64.31-37.35Q595.46-800 480-800ZM240-540h210v-149.23H240V-540Zm412.31 60H240h480-67.69ZM510-540h210v-149.23H510V-540ZM340-327.69q22.54 0 37.42-14.89 14.89-14.88 14.89-37.42t-14.89-37.42q-14.88-14.89-37.42-14.89t-37.42 14.89q-14.89 14.88-14.89 37.42t14.89 37.42q14.88 14.89 37.42 14.89Zm280 0q22.54 0 37.42-14.89 14.89-14.88 14.89-37.42t-14.89-37.42q-14.88-14.89-37.42-14.89t-37.42 14.89q-14.89 14.88-14.89 37.42t14.89 37.42q14.88 14.89 37.42 14.89ZM307.69-280h344.62q28.69 0 48.19-19.5Q720-319 720-347.69V-480H240v132.31q0 28.69 19.5 48.19Q279-280 307.69-280ZM480-749.23H711.39 251.08 480Z" />
                    </svg>
                    <Heading size="panel">{t('landing.achievements.useCasesLabel')}</Heading>
                  </div>
                  <Text>{t('landing.achievements.useCases')}</Text>
                </div>
              </Card>
              <Card tone="success" elevation="raised" padding="lg" gap="none" className={styles.statCard}>
                <div className={styles.statCardContent}>
                  <div className={styles.statIconAndHeading}>
                    <svg
                      className={styles.statIcon}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 -960 960 960"
                      fill="currentColor"
                    >
                      <path d="M480.38-140q-70.76 0-132.61-26.77-61.85-26.77-107.85-72.77-46-46-72.76-107.85-26.77-61.84-26.77-132.61 0-70.77 26.77-132.61 26.76-61.85 72.76-107.85 46-46 107.85-72.77Q409.62-820 480.38-820q75.47 0 143.39 31.73 67.92 31.73 116.61 89.19v-94.77h60v203.08H597.31v-60h104.61q-41.38-51-98.69-80.11Q545.92-760 480.38-760q-117 0-198.5 81.5T200.38-480q0 117 81.5 198.5t198.5 81.5q105 0 183.12-68.19T756.77-440H818q-14.62 128.92-111.15 214.46Q610.31-140 480.38-140Zm118.93-178.92L450.39-467.85V-680h59.99v187.85l131.08 131.07-42.15 42.16Z" />
                    </svg>
                    <Heading size="panel">{t('landing.achievements.futureUseCases')}</Heading>
                  </div>
                  <Text>
                    {t('landing.achievements.futureUseCasesLabel')}
                  </Text>
                </div>
              </Card>
              <Card tone="success" elevation="raised" padding="lg" gap="none" className={styles.statCard}>
                <div className={styles.statCardContent}>
                  <div className={styles.statIconAndHeading}>
                    <svg
                      className={styles.statIcon}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 -960 960 960"
                      fill="currentColor"
                    >
                      <path d="M612-553.46 749.92-692 725-716.92l-113 113-57-56.62L530.08-635 612-553.46ZM140-170v-60h460v60H140Zm500.06-280q-78.83 0-134.44-55.56Q450-561.12 450-639.94q0-78.83 55.56-134.44Q561.12-830 639.94-830q78.83 0 134.44 55.56Q830-718.88 830-640.06q0 78.83-55.56 134.44Q718.88-450 640.06-450ZM140-490v-60h233.54q5.46 16.62 12.34 31.42 6.89 14.81 16.43 28.58H140Zm0 160v-60h373.31q19.54 10.54 41.31 17.92 21.77 7.39 45.38 10.62V-330H140Z" />
                    </svg>
                    <Heading size="panel">{t('landing.achievements.formats')}</Heading>
                  </div>
                  <Text>{t('landing.achievements.formatsLabel')}</Text>
                </div>
              </Card>
            </div>
          </div>
          {/* Network Rail Illustration */}
          <div className={styles.heroIllustration}>
            <NetworkRailIllustration
              alt={t(
                'landing.nodesIllustration.alt',
                'Illustration showing nodes and edges over a railway'
              )}
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
        </div>
      </div>

      {/* Latest status report (authenticated users only) */}
      <LatestStatusReportCard />

      {/* RINF */}
      <div className={styles.rinfSection}>
        <div className={styles.rinfMap}>
          <img
            src="/svg/europe-map.svg"
            alt={t('landing.rinf.mapAlt', 'Map of Europe')} />
        </div>
        <div className={styles.rinfContent}>
          <div className={styles.rinfIntro}>
            <div className={styles.rinfIntroText}>
              <Heading level={2} className="bn-mbe-400">
                {t('landing.rinf.connectingTitle')}
              </Heading>
              <Text>
                {t('landing.rinf.connectingDescription')}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* RINF cards */}
      <div className={styles.rinfBenefitsOuter}>
        <div className={styles.rinfBenefitsInner}>
          <div className={styles.rinfBenefits}>
            <Card elevation="raised" padding="lg" gap="none">
              <Heading size="panel" className="bn-mbe-200">
                {t('landing.rinf.benefits.microTopology')}
              </Heading>
              <Text>
                {t('landing.rinf.benefits.microTopologyDesc')}
              </Text>
            </Card>
            <Card elevation="raised" padding="lg" gap="none">
              <Heading size="panel" className="bn-mbe-200">
                {t('landing.rinf.benefits.eraMapping')}
              </Heading>
              <Text>
                {t('landing.rinf.benefits.eraMappingDesc')}
              </Text>
            </Card>
            <Card elevation="raised" padding="lg" gap="none">
              <Heading size="panel" className="bn-mbe-200">
                {t('landing.rinf.benefits.shaclInferenceValidation')}
              </Heading>
              <Text>
                {t('landing.rinf.benefits.shaclInferenceValidationDesc')}
              </Text>
            </Card>
            <Card elevation="raised" padding="lg" gap="none">
              <Heading size="panel" className="bn-mbe-200">
                {t('landing.rinf.benefits.coverageProgress')}
              </Heading>
              <Text>
                {t('landing.rinf.benefits.coverageProgressDesc')}
              </Text>
            </Card>
          </div>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* From challenge to solution section */}
      <div className="bn-mbs-2000 bn-mbe-1500">
        <div className={styles.sectionChallengeSolutionHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerImage}>
              <img
                src="/svg/norway_ink.svg"
                alt={t(
                  'landing.norwayNetwork.alt',
                  'Railway network illustration showing interconnected nodes'
                )}
              />
            </div>
            <div className={styles.headerText}>
              <Heading level={2} className="bn-mbe-600">
                {t('landing.overview.title')}
              </Heading>
              <Text variant="intro" className="bn-mbe-1200">
                {t('landing.overview.tagline')}
              </Text>
            </div>
          </div>
        </div>

        <div className={`${styles.calloutGrid} ${styles.columns2}`}>
          <div>
            <Heading level={3} size="section" className="bn-mbe-400">
              {t('landing.overview.challenges.title')}
            </Heading>
            <ul>
              <li className="bn-mbe-200">
                <Text>{t('landing.overview.challenges.item1')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>{t('landing.overview.challenges.item2')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>{t('landing.overview.challenges.item3')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>{t('landing.overview.challenges.item4')}</Text>
              </li>
            </ul>
          </div>

          <div>
            <Heading level={3} size="section" className="bn-mbe-400">
              {t('landing.overview.solution.title')}
            </Heading>
            <ul>
              <li className="bn-mbe-200">
                <Text>{t('landing.overview.solution.item1')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>{t('landing.overview.solution.item2')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>{t('landing.overview.solution.item3')}</Text>
              </li>
              <li className="bn-mbe-200">
                <Text>{t('landing.overview.solution.item4')}</Text>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Vision & Purpose */}
      <div className={`${styles.calloutGrid} ${styles.columns2} ${styles.visionAndPurpose}`}>
        <div>
          <Card tone="success" elevation="raised" padding="lg" gap="none">
            <Heading level={3} size="section" className="bn-mbe-400">
              {t('landing.vision.title')}
            </Heading>
            <Text>{t('landing.vision.text')}</Text>
          </Card>
        </div>
        <div>
          <Card tone="success" elevation="raised" padding="lg" gap="none">
            <Heading level={3} size="section" className="bn-mbe-400">
              {t('landing.purpose.title')}
            </Heading>
            <Text>{t('landing.purpose.text')}</Text>
          </Card>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* Facts about our knowledge graph*/}
      <div className="bn-mbs-2000 bn-mbe-2000">
        <Heading level={2} className="bn-mbe-600">
          {t('landing.knowledgeGraph.title')}
        </Heading>
        <Text className="bn-mbe-1200">{t('landing.knowledgeGraph.text')}</Text>
        <div className={styles.factsContainer}>
          <div className={styles.factsStatsCards}>
            <Card tone="success" elevation="raised" padding="lg" gap="none" className={styles.statCard}>
              <div className={styles.statCardContent}>
                <div className={styles.statIconAndHeading}>
                  <svg
                    className={styles.statIcon}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 -960 960 960"
                    fill="currentColor"
                  >
                    <path d="M480.05-100q-44.57 0-75.77-31.19-31.2-31.18-31.2-75.73 0-6.16.88-12.73.89-6.58 2.66-12.74l-94.93-53.53q-14.46 14.38-33.6 22.26-19.15 7.89-41.17 7.89-44.55 0-75.73-31.28Q100-318.33 100-363.01q0-44.68 31.19-76.03 31.18-31.34 75.73-31.34 23.16 0 43.43 9.19t35.34 25.58l129-65q-4.54-23.39 1.35-46.16 5.88-22.77 21.42-41l-42.07-63.15q-7.39 2.77-15.64 4.15-8.25 1.38-17.06 1.38-44.55 0-75.73-31.27-31.19-31.28-31.19-75.96 0-44.69 31.2-76.03Q318.17-860 362.74-860q44.57 0 76.11 31.3 31.53 31.3 31.53 76.01 0 19.61-6.88 37.34-6.89 17.74-18.65 31.58l42.69 63.16q8-2.77 15.61-4.16 7.61-1.38 16.31-1.38 16.86 0 31.74 4.57 14.88 4.58 27.72 13.35l76-62.08q-4.38-10-6.96-20.5-2.57-10.5-2.57-21.88 0-44.71 31.19-76.01 31.2-31.3 75.77-31.3 44.57 0 76.11 31.28T860-712.76q0 44.68-31.54 76.03-31.54 31.34-76.15 31.34-17 0-31.62-5.07-14.61-5.08-27.46-13.85l-75.62 62.69q4.77 10.19 7.16 20.88 2.38 10.69 2.38 21.89 0 44.72-31.54 76.01-31.53 31.3-76.15 31.3-23.23 0-43.38-9.38-20.16-9.39-35.23-25.77l-128.39 64.38q3.15 10.92 2.4 21.98-.76 11.05-3.78 22.1l95.15 54.15q14.46-14.38 33.12-22.27 18.65-7.88 40.65-7.88 44.61 0 76.15 31.2 31.54 31.2 31.54 75.77 0 44.57-31.54 75.91Q524.61-100 480.05-100ZM207.12-315.77q19.88 0 33.69-13.59 13.81-13.6 13.81-33.7 0-20.09-13.72-33.9-13.71-13.81-33.98-13.81-19.69 0-33.3 13.71Q160-383.35 160-363.08q0 19.7 13.62 33.5 13.61 13.81 33.5 13.81Zm155.76-389.61q19.89 0 33.7-13.6 13.81-13.59 13.81-33.69 0-20.1-13.72-33.71Q382.96-800 362.69-800q-19.69 0-33.31 13.81-13.61 13.81-13.61 33.5 0 19.69 13.61 33.5 13.62 13.81 33.5 13.81Zm117.31 545.76q19.89 0 33.5-13.59 13.62-13.6 13.62-33.69 0-20.1-13.6-33.72-13.6-13.61-33.71-13.61-19.69 0-33.5 13.81-13.81 13.8-13.81 33.5 0 19.69 13.81 33.5 13.81 13.8 33.69 13.8Zm38.85-311.92q19.88 0 33.5-13.59 13.61-13.6 13.61-33.7 0-20.09-13.6-33.71-13.6-13.61-33.7-13.61-19.7 0-33.5 13.8-13.81 13.81-13.81 33.5 0 19.7 13.81 33.5 13.8 13.81 33.69 13.81ZM752.5-665.38q19.88 0 33.69-13.6Q800-692.57 800-712.67q0-20.1-13.71-33.71Q772.58-760 752.31-760q-19.7 0-33.31 13.6-13.62 13.6-13.62 33.71 0 19.69 13.62 33.5 13.61 13.81 33.5 13.81Z" />
                  </svg>
                  <Heading size="panel">{t('landing.knowledgeGraph.triples')}</Heading>
                </div>
                <Text>{t('landing.knowledgeGraph.triplesLabel')}</Text>
              </div>
            </Card>
            <Card tone="success" elevation="raised" padding="lg" gap="none" className={styles.statCard}>
              <div className={styles.statCardContent}>
                <div className={styles.statIconAndHeading}>
                  <svg
                    className={styles.statIcon}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 -960 960 960"
                    fill="currentColor"
                  >
                    <path d="M219.23-100q-49.15 0-84.19-35.04T100-219.23q0-49.15 35.04-84.19t84.19-35.04q17.6 0 34.22 5.27 16.63 5.27 30.47 14.81L450-484.46v-141.16q-38.61-9.92-63.92-41.66-25.31-31.75-25.31-73.49 0-49.15 35.04-84.19T480-860q49.15 0 84.19 35.04t35.04 84.19q0 41.74-25.12 73.49Q549-635.54 510-625.62v141.16l166.69 166.08q13.85-9.54 30.1-14.81 16.25-5.27 33.98-5.27 49.15 0 84.19 35.04T860-219.23q0 49.15-35.04 84.19T740.77-100q-49.15 0-84.19-35.04t-35.04-84.19q0-14.94 3.54-29.05 3.54-14.1 10.38-26.41L480-430.15 324.54-274.69q6.84 12.31 10.38 26.41 3.54 14.11 3.54 29.05 0 49.15-35.04 84.19T219.23-100Zm521.49-60q24.66 0 41.97-17.26T800-219.19q0-24.66-17.26-41.96-17.26-17.31-41.93-17.31-24.66 0-41.96 17.26-17.31 17.26-17.31 41.92t17.26 41.97Q716.06-160 740.72-160ZM479.95-681.54q24.67 0 41.97-17.26 17.31-17.26 17.31-41.92t-17.26-41.97Q504.71-800 480.05-800q-24.67 0-41.97 17.26-17.31 17.26-17.31 41.93 0 24.66 17.26 41.96 17.26 17.31 41.92 17.31ZM219.19-160q24.66 0 41.96-17.26 17.31-17.26 17.31-41.93 0-24.66-17.26-41.96-17.26-17.31-41.92-17.31t-41.97 17.26Q160-243.94 160-219.28t17.26 41.97Q194.52-160 219.19-160Z" />
                  </svg>
                  <Heading size="panel">{t('landing.knowledgeGraph.ontologies')}</Heading>
                </div>
                <Text>
                  {t('landing.knowledgeGraph.ontologiesLabel')}
                </Text>
              </div>
            </Card>
            <Card tone="success" elevation="raised" padding="lg" gap="none" className={styles.statCard}>
              <div className={styles.statCardContent}>
                <div className={styles.statIconAndHeading}>
                  <svg
                    className={styles.statIcon}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 -960 960 960"
                    fill="currentColor"
                  >
                    <path d="M360-130q-62.15 0-106.08-43.92Q210-217.85 210-280v-333.92q-35-13-57.5-41.58T130-719.87q0-45.89 32.07-78.01Q194.14-830 239.95-830q45.82 0 77.93 32.12Q350-765.76 350-719.87q0 35.79-22.5 64.37-22.5 28.58-57.5 41.58V-280q0 37.13 26.46 63.56Q322.92-190 360.07-190q37.16 0 63.55-26.44Q450-242.87 450-280v-400q0-62.15 43.92-106.08Q537.85-830 600-830t106.08 43.92Q750-742.15 750-680v333.92q35 13 57.5 41.58t22.5 64.37q0 45.89-32.07 78.01Q765.86-130 720.05-130q-45.82 0-77.93-32.12Q610-194.24 610-240.13q0-35.79 22.5-64.87 22.5-29.08 57.5-41.08V-680q0-37.13-26.46-63.56Q637.08-770 599.93-770q-37.16 0-63.55 26.44Q510-717.13 510-680v400q0 62.15-43.92 106.08Q422.15-130 360-130ZM240-670q20.85 0 35.42-14.58Q290-699.15 290-720t-14.58-35.42Q260.85-770 240-770t-35.42 14.58Q190-740.85 190-720t14.58 35.42Q219.15-670 240-670Zm480 480q20.85 0 35.42-14.58Q770-219.15 770-240t-14.58-35.42Q740.85-290 720-290t-35.42 14.58Q670-260.85 670-240t14.58 35.42Q699.15-190 720-190ZM240-720Zm480 480Z" />
                  </svg>
                  <Heading size="panel">{t('landing.knowledgeGraph.tracks')}</Heading>
                </div>
                <Text>{t('landing.knowledgeGraph.tracksLabel')}</Text>
              </div>
            </Card>
          </div>
          <div className={styles.kgImage}>
            <img
              src="/svg/knowledgeGraph_ink.svg"
              alt={t(
                'landing.knowledgeGraphIllustration.alt',
                'Knowledge graph illustration showing interconnected railway concepts'
              )}
              className={styles.lightThemeImage}
            />
            <img
              src="/svg/knowledgeGraph_dark_ink.svg"
              alt={t(
                'landing.knowledgeGraphIllustration.alt',
                'Knowledge graph illustration showing interconnected railway concepts'
              )}
              className={styles.darkThemeImage}
            />
          </div>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* DIM Concepts Section */}
      <div className="bn-mbs-2000 bn-mbe-2000">
        <Heading level={2} className="bn-mbe-600">
          {t('landing.concepts.title')}
        </Heading>
        <Text variant="intro" className="bn-mbe-1200">
          {t('landing.concepts.subtitle')}
        </Text>
        <div className={styles.conceptsGrid}>
          {[
            { key: 'knowledgeGraph' },
            { key: 'triple' },
            { key: 'rdf' },
            { key: 'ontology' },
            { key: 'sparql' },
            { key: 'shacl' },
            { key: 'iri' },
            { key: 'linkedData' },
          ].map(({ key }) => (
            <Card key={key} tone="info" elevation="raised" padding="lg" gap="none">
              <Heading size="panel" className="bn-mbe-200">
                {t(`landing.concepts.${key}`)}
              </Heading>
              <Text>{t(`landing.concepts.${key}Desc`)}</Text>
            </Card>
          ))}
        </div>
        <div className="bn-mbs-800">
          <RouterLink to={routes.helpDefinitions}>
            {t('landing.concepts.learnMore')} →
          </RouterLink>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* Quality Quote - Highlighted */}
      <div className="bn-mbs-2000 bn-mbe-1500">
        <Heading level={2} className="bn-mbe-600">
          {t('landing.quality.title')}
        </Heading>
        <Text variant="intro" className="bn-mbe-400">
          "{t('landing.quality.quote')}"
        </Text>
        <Text>{t('landing.quality.message')}</Text>
      </div>

      {/* Learn More About DIM Section */}
      <div className="bn-mbs-2000 bn-mbe-1500">
        <div className={styles.sectionChallengeSolutionHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerText}>
              <Heading level={2} className="bn-mbe-600">
                {t('landing.presentations.title')}
              </Heading>
              <Text variant="intro" className="bn-mbe-200">
                {t('landing.presentations.description-line-1')}
              </Text>
              <Text variant="intro" className="bn-mbe-200">
                {t('landing.presentations.description-line-2')}
              </Text>
              <Text variant="intro">
                {t('landing.repository.visit-the')} {' '}
                <RouterLink to={routes.helpDataLineage}>{t('landing.repository.dim-data-lineage')}</RouterLink>{' '}
                {t('landing.repository.for-more-information')}
              </Text>
            </div>
            <div className={styles.headerImageLarge}>
              <img
                className={styles.eraImage}
                src="/ERA-433.jpg"
                alt={t('landing.eraImage.alt', 'DIM presenting at ERA Rail Data Forum 2025')}
              />
              <Text variant="table" className={styles.imageCopyright}>
                © ERA
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Presentations */}
      <div className="bn-mbe-2000">
        <div className={`${styles.calloutGrid} ${styles.columns2}`}>
          <div>
            <Card tone="info" elevation="raised" padding="lg" gap="none">
              <Heading level={3} size="panel" className="bn-mbe-200">
                {t('landing.presentations.eraRailDataForum.title')}
              </Heading>
              <Text weight="bold" className="bn-mbe-400">
                {t('landing.presentations.eraRailDataForum.date')}
              </Text>
              <Text className="bn-mbe-400">
                {t('landing.presentations.eraRailDataForum.description')}
              </Text>
              <ButtonLink
                iconStart={Download}
                href="/presentations/2025-06-12 - ERA Rail Data Forum (RDF).pdf"
              >
                {t('landing.presentations.download')}
              </ButtonLink>
            </Card>
          </div>
          <div>
            <Card tone="success" elevation="raised" padding="lg" gap="none">
              <Heading level={3} size="panel" className="bn-mbe-200">
                {t('landing.presentations.knowledgeGraphOslo.title')}
              </Heading>
              <Text weight="bold" className="bn-mbe-400">
                {t('landing.presentations.knowledgeGraphOslo.date')}
              </Text>
              <Text className="bn-mbe-400">
                {t('landing.presentations.knowledgeGraphOslo.description')}
              </Text>
              <ButtonLink
                iconStart={Download}
                href="/presentations/2025-11-24 - Meet-up Knowledge Graph Oslo.pdf"
              >
                {t('landing.presentations.download')}
              </ButtonLink>
            </Card>
          </div>
          {user?.isAuthenticated && (
            <div>
              <Card tone="info" elevation="raised" padding="lg" gap="none">
                <Heading level={3} size="panel" className="bn-mbe-200">
                  {t('landing.presentations.linkedRailwayData.title')}
                </Heading>
                <Text weight="bold" className="bn-mbe-400">
                  {t('landing.presentations.linkedRailwayData.date')}
                </Text>
                <Text className="bn-mbe-400">
                  {t('landing.presentations.linkedRailwayData.description')}
                </Text>
                <div className={styles.buttonGroup}>
                  <ButtonLink
                    iconStart={Download}
                    href="/private/presentations/Hva om jernbanedataene v%C3%A5re hang sammen.pptx"
                  >
                    {t('landing.presentations.download')}
                  </ButtonLink>
                  <ButtonLink
                    iconStart={Download}
                    href="/download/data/presentations/Hva om jernbanedataene v%C3%A5re hang sammen.mp4"
                  >
                    {t('landing.presentations.downloadVideo')}
                  </ButtonLink>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default LandingPage;
